import React, { useState } from "react";
import { Box, Typography, Rating, useTheme, useMediaQuery, IconButton, Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";

const MediaContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "16/9",
  position: "relative",
  overflow: "hidden",
  borderRadius: theme.breakpoints.down("sm") ? "12px" : "16px 16px 0 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.6) 100%)",
  border: "1px solid rgba(226,232,240,0.3)",
}));

const StyledIframe = styled('iframe')(({ theme }) => ({
  width: "100%",
  height: "100%",
  border: "none",
  borderRadius: "16px 16px 0 0",
}));

const DomainLabel = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.breakpoints.down("sm") ? "6px" : "8px",
  left: theme.breakpoints.down("sm") ? "6px" : "8px",
  color: "white",
  background: "linear-gradient(135deg, rgba(99,102,241,0.9) 0%, rgba(139,92,246,0.9) 100%)",
  padding: theme.breakpoints.down("sm") ? "6px 10px" : "4px 8px",
  borderRadius: theme.breakpoints.down("sm") ? "8px" : "10px",
  fontSize: theme.breakpoints.down("sm") ? "0.75rem" : "10px",
  fontWeight: 600,
  maxWidth: theme.breakpoints.down("sm") ? "70%" : "60%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  minHeight: theme.breakpoints.down("sm") ? "32px" : "auto",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    background: "linear-gradient(135deg, rgba(88,85,235,0.95) 0%, rgba(124,58,237,0.95) 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
  },
  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0 2px 6px rgba(99,102,241,0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: "44px",
    padding: "8px 12px",
  },
}));

const RatingLabel = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.breakpoints.down("sm") ? "6px" : "8px",
  right: theme.breakpoints.down("sm") ? "6px" : "8px",
  background: "linear-gradient(135deg, rgba(255,180,0,0.9) 0%, rgba(255,193,7,0.9) 100%)",
  padding: theme.breakpoints.down("sm") ? "6px 10px" : "4px 8px",
  borderRadius: theme.breakpoints.down("sm") ? "8px" : "10px",
  fontSize: theme.breakpoints.down("sm") ? "0.75rem" : "11px",
  fontWeight: 600,
  color: "white",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 2px 8px rgba(255,180,0,0.3)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  gap: theme.breakpoints.down("sm") ? "6px" : "4px",
  minHeight: theme.breakpoints.down("sm") ? "32px" : "auto",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(255,180,0,0.4)",
  },
  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0 2px 6px rgba(255,180,0,0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: "44px",
    padding: "8px 12px",
  },
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.breakpoints.down("sm") ? "6px" : "8px",
  left: theme.breakpoints.down("sm") ? "6px" : "8px",
  display: "flex",
  alignItems: "center",
  gap: theme.breakpoints.down("sm") ? "6px" : "8px",
  background: "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)",
  padding: theme.breakpoints.down("sm") ? "8px 12px" : "6px 10px",
  borderRadius: theme.breakpoints.down("sm") ? "10px" : "12px",
  border: "1px solid rgba(255,255,255,0.3)",
  boxShadow: "0 4px 20px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
  backdropFilter: "blur(25px)",
  WebkitBackdropFilter: "blur(25px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  maxWidth: theme.breakpoints.down("sm") ? "180px" : "200px",
  minHeight: theme.breakpoints.down("sm") ? "40px" : "auto",
  "&:hover": {
    transform: "translateY(-2px)",
    background: "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.25) 100%)",
    boxShadow: "0 8px 30px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.3)",
    borderColor: "rgba(255,255,255,0.4)",
  },
  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0 4px 15px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: "120px",
  },
}));

const UserAvatar = styled(Box)(({ theme }) => ({
  width: theme.breakpoints.down("sm") ? "28px" : "24px",
  height: theme.breakpoints.down("sm") ? "28px" : "24px",
  borderRadius: "50%",
  border: "1px solid rgba(255,255,255,0.4)",
  background: "linear-gradient(135deg, rgba(99,102,241,0.8) 0%, rgba(139,92,246,0.8) 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)",
}));

const UserNameText = styled(Typography)(({ theme }) => ({
  fontSize: theme.breakpoints.down("sm") ? "0.8125rem" : "11px",
  fontWeight: 700,
  color: "rgba(255,255,255,0.95)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: theme.breakpoints.down("sm") ? "120px" : "150px",
  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
}));

const FallbackContainer = styled(Box)(({ theme, color }) => ({
  width: "100%",
  aspectRatio: "16/9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
  color: "#fff",
  fontSize: theme.breakpoints.down("sm") ? "36px" : "48px",
  fontWeight: "bold",
  borderRadius: theme.breakpoints.down("sm") ? "12px" : "16px 16px 0 0",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(226,232,240,0.3)",
  padding: "6px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "6px",
    left: "6px",
    right: "6px",
    bottom: "6px",
    background: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(10px)",
    borderRadius: theme.breakpoints.down("sm") ? "6px" : "10px 10px 0 0",
  },
}));

const FallbackLetter = styled(Typography)(({ theme }) => ({
  fontSize: theme.breakpoints.down("sm") ? "36px" : "48px",
  fontWeight: "bold",
  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
  zIndex: 1,
  position: "relative",
  width: "auto",
  display: "inline-block",
  marginTop: theme.breakpoints.down("sm") ? "12px" : "16px",
  marginLeft: theme.breakpoints.down("sm") ? "12px" : "16px",
  marginRight: theme.breakpoints.down("sm") ? "12px" : "16px",
  marginBottom: "6px",
}));

function getDomain(url) {
  if (!url) return "";
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  } catch {
    return "";
  }
}

function isYouTubeUrl(url) {
  if (!url) return false;
  try {
    const domain = getDomain(url);
    return domain.includes("youtube.com") || domain.includes("youtu.be");
  } catch {
    return false;
  }
}

function isYouTubeChannel(url) {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    // YouTube channel URLs typically contain /channel/, /c/, /user/, or /@
    return urlObj.pathname.includes('/channel/') || 
           urlObj.pathname.includes('/c/') || 
           urlObj.pathname.includes('/user/') || 
           urlObj.pathname.includes('/@');
  } catch {
    return false;
  }
}

function isYouTubeVideo(url) {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    // YouTube video URLs contain /watch with v parameter or youtu.be with video ID
    return (urlObj.hostname.includes("youtube.com") && urlObj.pathname === "/watch" && urlObj.searchParams.has("v")) ||
           (urlObj.hostname.includes("youtu.be") && urlObj.pathname.length > 1);
  } catch {
    return false;
  }
}

function getYouTubeEmbedUrl(url) {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${urlObj.pathname.slice(1)}`;
    }
    if (urlObj.hostname.includes("youtube.com") && urlObj.searchParams.has("v")) {
      return `https://www.youtube.com/embed/${urlObj.searchParams.get("v")}`;
    }
  } catch (e) {
    return "";
  }
  return "";
}

function getColorForUrl(url) {
  const colors = [
    "#ADD8E6", // Light Blue
    "#20B2AA", // Light Sea Green
    "#5F9EA0", // Cadet Blue
    "#00CED1", // Dark Turquoise
    "#7FFFD4", // Aquamarine
    "#4682B4", // Steel Blue
  ];

  if (!url) return colors[0];
  try {
    const domain = new URL(url).hostname.replace("www.", "");
    const firstLetter = domain[0]?.toLowerCase() || "a";
    return colors[firstLetter.charCodeAt(0) % colors.length];
  } catch {
    return colors[0];
  }
}

const HomeCardMedia = ({ data, minHeight, maxHeight, hideUserInfo = false, onEditImage, canEdit = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const domain = data?.page_id?.href ? getDomain(data.page_id.href) : "";
  const color = getColorForUrl(data?.page_id?.href);
  const isSystemAgent = data.user_name === "system_recommendation_agent";
  const [imageError, setImageError] = useState(false);

  const handleDomainClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.page_id?.href) {
      window.open(data.page_id.href, "_blank");
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getFirstLetter = () => {
    if (domain) {
      return domain.replace("www.", "")[0]?.toUpperCase() || "?";
    }
    return "?";
  };

  // If the content is a YouTube video
  if (data?.page_id?.href && isYouTubeUrl(data.page_id.href)) {
    // If it's a YouTube channel, use the image_url instead of iframe
    if (isYouTubeChannel(data.page_id.href)) {
      // For YouTube channels, fall through to the image_url handling below
      // This will use the channel image if available, or fallback
    } else if (isYouTubeVideo(data.page_id.href)) {
      // Only show iframe for actual YouTube videos
      const embedUrl = getYouTubeEmbedUrl(data.page_id.href);
      return (
        <MediaContainer>
          <StyledIframe
            src={embedUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          {domain && (
            <DomainLabel onClick={handleDomainClick} title={data?.page_id?.href}>
              {domain}
            </DomainLabel>
          )}
        </MediaContainer>
      );
    }
  }

  // If there is an image_url
  if (data?.image_url && !imageError) {
    return (
      <MediaContainer>
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "16px 16px 0 0",
            background: "linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.8) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            padding: "6px",
            position: "relative",
          }}
        >
          <img
            src={data.image_url}
            alt={data.name || "Card image"}
            onError={handleImageError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
            }}
          />
          {canEdit && onEditImage && (
            <Tooltip title="Edit image" arrow>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditImage();
                }}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  backgroundColor: "white",
                  color: "#667eea",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 10,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "scale(1.1)",
                  },
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  transition: "all 0.2s ease",
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {domain && (
          <DomainLabel onClick={handleDomainClick} title={data?.page_id?.href}>
            {domain}
          </DomainLabel>
        )}
      </MediaContainer>
    );
  }

  // If image failed to load or no image_url, show fallback with letter
  if (data?.image_url && imageError) {
    const letter = getFirstLetter();
    return (
      <FallbackContainer color={color}>
        {/* Domain label */}
        {domain && (
          <DomainLabel onClick={handleDomainClick} title={data?.page_id?.href}>
            {domain}
          </DomainLabel>
        )}
        <FallbackLetter>{letter}</FallbackLetter>
        {canEdit && onEditImage && (
          <Tooltip title="Edit image" arrow>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditImage();
              }}
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                backgroundColor: "white",
                color: "#667eea",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  transform: "scale(1.1)",
                },
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                transition: "all 0.2s ease",
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </FallbackContainer>
    );
  }

  // Fallback with color and first letter
  const letter = domain.replace("www.", "")[0]?.toUpperCase() || "?";
  return (
    <FallbackContainer color={color}>
      {/* Domain label */}
      {domain && (
        <DomainLabel onClick={handleDomainClick} title={data?.page_id?.href}>
          {domain}
        </DomainLabel>
      )}
      <FallbackLetter>{letter}</FallbackLetter>
      {canEdit && onEditImage && (
        <Tooltip title="Edit image" arrow>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditImage();
            }}
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "white",
              color: "#667eea",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                transform: "scale(1.1)",
              },
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              transition: "all 0.2s ease",
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </FallbackContainer>
  );
};

export default HomeCardMedia;
